import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  ICreateHomeworkPayload,
  ICreateHomeworkResponse,
  IHomeworkDashboardQueryParams,
  IHomeworkDashboardResponse,
  IGetHomeworkByIdResponse,
  IEditHomeworkPayload,
  IEditHomeworkResponse,
  IHomeworkStatusChangeResponse,
  ITaskSubmissionsResponse,
  ITaskSubmissionsQueryParams,
  IGradeTaskSubmissionPayload,
  IReleaseTaskResponse,
} from '../../interface/homework.interface';
import { IErrorResponse } from '../../interface/main.interface';
import { homeworkService } from '../../services/homework';

export const fetchHomeworkDashboard = createAsyncThunk<
  IHomeworkDashboardResponse,
  IHomeworkDashboardQueryParams | undefined,
  { rejectValue: IErrorResponse }
>('homework/fetch-dashboard', async (params, { rejectWithValue }) => {
  try {
    const response = await homeworkService.fetchHomeworkDashboard(params);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const createHomework = createAsyncThunk<
  ICreateHomeworkResponse,
  ICreateHomeworkPayload,
  { rejectValue: IErrorResponse }
>('homework/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await homeworkService.createHomework(payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const getHomeworkById = createAsyncThunk<
  IGetHomeworkByIdResponse,
  string,
  { rejectValue: IErrorResponse }
>('homework/get-by-id', async (homeworkId, { rejectWithValue }) => {
  try {
    const response = await homeworkService.getHomeworkById(homeworkId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const editHomework = createAsyncThunk<
  IEditHomeworkResponse,
  { homeworkId: string; payload: IEditHomeworkPayload },
  { rejectValue: IErrorResponse }
>('homework/edit', async ({ homeworkId, payload }, { rejectWithValue }) => {
  try {
    const response = await homeworkService.editHomework(homeworkId, payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const duplicateHomework = createAsyncThunk<
  ICreateHomeworkResponse,
  string,
  { rejectValue: IErrorResponse }
>('homework/duplicate', async (homeworkId, { rejectWithValue }) => {
  try {
    const response = await homeworkService.duplicateHomework(homeworkId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const publishHomework = createAsyncThunk<
  IHomeworkStatusChangeResponse,
  string,
  { rejectValue: IErrorResponse }
>('homework/publish', async (homeworkId, { rejectWithValue }) => {
  try {
    const response = await homeworkService.publishHomework(homeworkId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const startGrading = createAsyncThunk<
  IHomeworkStatusChangeResponse,
  string,
  { rejectValue: IErrorResponse }
>('homework/start-grading', async (homeworkId, { rejectWithValue }) => {
  try {
    const response = await homeworkService.startGrading(homeworkId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const fetchTaskSubmissions = createAsyncThunk<
  ITaskSubmissionsResponse,
  {
    homeworkId: string;
    detailId: string;
    params?: ITaskSubmissionsQueryParams;
  },
  { rejectValue: IErrorResponse }
>(
  'homework/task-submissions',
  async ({ homeworkId, detailId, params }, { rejectWithValue }) => {
    try {
      const response = await homeworkService.fetchTaskSubmissions(
        homeworkId,
        detailId,
        params,
      );
      return response.data;
    } catch (err) {
      const error = err as AxiosError<IErrorResponse>;
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  },
);

export const gradeTaskSubmission = createAsyncThunk<
  IHomeworkStatusChangeResponse,
  {
    homeworkId: string;
    detailId: string;
    studentId: string;
    payload: IGradeTaskSubmissionPayload;
  },
  { rejectValue: IErrorResponse }
>(
  'homework/grade-task-submission',
  async ({ homeworkId, detailId, studentId, payload }, { rejectWithValue }) => {
    try {
      const response = await homeworkService.gradeTaskSubmission(
        homeworkId,
        detailId,
        studentId,
        payload,
      );
      return response.data;
    } catch (err) {
      const error = err as AxiosError<IErrorResponse>;
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  },
);

export const releaseTask = createAsyncThunk<
  IReleaseTaskResponse,
  { homeworkId: string; detailId: string },
  { rejectValue: IErrorResponse }
>(
  'homework/release-task',
  async ({ homeworkId, detailId }, { rejectWithValue }) => {
    try {
      const response = await homeworkService.releaseTask(homeworkId, detailId);
      return response.data;
    } catch (err) {
      const error = err as AxiosError<IErrorResponse>;
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  },
);

export const homeworkActions = {
  fetchHomeworkDashboard,
  createHomework,
  getHomeworkById,
  editHomework,
  duplicateHomework,
  publishHomework,
  startGrading,
  fetchTaskSubmissions,
  gradeTaskSubmission,
  releaseTask,
};